import { useCallback, useEffect, useRef, useState } from "react";
import { mappingApi } from "@services/mappingApi";
import { mappingConfigApi } from "@services/mappingConfigApi";
import { errorMessage, useApiResource, type ApiResource } from "@hooks/monitoring/useApiResource";
import type {
  BatchTargetList,
  ConfigDiff,
  DiffAgainst,
  PluginConfigVersion,
  ServiceList,
  ServiceSchema,
  VersionListItem,
} from "@models/mappingConfig";
import { isNotFound } from "@utils/mappingConfig/paths";

/** Every service in the registry with the active version of each plugin. */
export const useMappingServices = (): ApiResource<ServiceList> =>
  useApiResource("mapping-config:services", (signal) => mappingConfigApi.listServices(signal));

/** Mapping targets a doctype can be routed to; loads only while `enabled`. */
export const useMappingBatchTargets = (enabled: boolean): ApiResource<BatchTargetList> =>
  useApiResource(enabled ? "mapping:batch-targets" : null, (signal) => mappingApi.getBatchTargets(signal));

export const useConfigSchema = (service: string | undefined): ApiResource<ServiceSchema> =>
  useApiResource(service ? `schema:${service}` : null, (signal) => mappingConfigApi.getSchema(service!, signal));

export const useConfigVersion = (
  service: string | undefined,
  pluginId: string | undefined,
  versionId: string | undefined,
): ApiResource<PluginConfigVersion> =>
  useApiResource(
    service && pluginId && versionId ? `version:${service}:${pluginId}:${versionId}` : null,
    (signal) => mappingConfigApi.getVersion(service!, pluginId!, versionId!, signal),
  );

/** The active version, or `null` when the plugin has none yet (the API answers 404). */
export const useActiveVersion = (
  service: string | undefined,
  pluginId: string | undefined,
): ApiResource<PluginConfigVersion | null> =>
  useApiResource(service && pluginId ? `active:${service}:${pluginId}` : null, async (signal) => {
    try {
      return await mappingConfigApi.getActive(service!, pluginId!, signal);
    } catch (error) {
      if (isNotFound(error)) return null;
      throw error;
    }
  });

export const useConfigDiff = (
  service: string | undefined,
  pluginId: string | undefined,
  versionId: string | undefined,
  against: DiffAgainst = "active",
): ApiResource<ConfigDiff> =>
  useApiResource(
    service && pluginId && versionId && against ? `diff:${service}:${pluginId}:${versionId}:${against}` : null,
    (signal) => mappingConfigApi.getDiff(service!, pluginId!, versionId!, against, signal),
  );

export interface VersionsState {
  items: VersionListItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  reload: () => void;
}

/** Versions, newest first, one page at a time (`next_cursor`); `loadMore` appends the next page. */
export const useConfigVersions = (
  service: string | undefined,
  pluginId: string | undefined,
  pageSize = 50,
): VersionsState => {
  const [items, setItems] = useState<VersionListItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const controller = useRef<AbortController | null>(null);

  const fetchPage = useCallback(
    (pageCursor: string | null, append: boolean) => {
      if (!service || !pluginId) return;
      controller.current?.abort();
      const current = new AbortController();
      controller.current = current;
      setLoading(true);
      setError(null);
      mappingConfigApi.listVersions(service, pluginId, { limit: pageSize, cursor: pageCursor }, current.signal).then(
        (page) => {
          if (current.signal.aborted) return;
          setItems((previous) => (append ? [...previous, ...page.items] : page.items));
          setCursor(page.next_cursor);
          setLoading(false);
        },
        (err: unknown) => {
          if (current.signal.aborted) return;
          setError(errorMessage(err));
          setLoading(false);
        },
      );
    },
    [service, pluginId, pageSize],
  );

  useEffect(() => {
    setItems([]);
    setCursor(null);
    fetchPage(null, false);
    return () => controller.current?.abort();
  }, [fetchPage, nonce]);

  const loadMore = useCallback(() => {
    if (cursor && !loading) fetchPage(cursor, true);
  }, [cursor, loading, fetchPage]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { items, loading, error, hasMore: cursor !== null, loadMore, reload };
};
